import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  PiggyBank,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Ban,
  IndianRupee,
  TrendingUp,
} from 'lucide-react';

export default function SettlementsPage() {
  const { api, merchant } = useAuth();
  const [settlements, setSettlements] = useState([]);
  const [rollingReserve, setRollingReserve] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settlementsRes, reserveRes] = await Promise.all([
          api.get('/settlements?limit=50'),
          api.get('/settlements/rolling-reserve')
        ]);
        setSettlements(settlementsRes.data);
        setRollingReserve(reserveRes.data);
      } catch (error) {
        toast.error('Failed to fetch settlement data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30', icon: Clock },
      scheduled: { className: 'bg-blue-500/20 text-blue-500 border-blue-500/30', icon: Calendar },
      processed: { className: 'bg-green-500/20 text-green-500 border-green-500/30', icon: CheckCircle },
      on_hold: { className: 'bg-red-500/20 text-red-500 border-red-500/30', icon: Ban },
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant="outline" className={`${config.className} rounded-sm`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="card-grid">
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const pendingSettlements = settlements.filter(s => s.status === 'pending' || s.status === 'scheduled');
  const totalPending = pendingSettlements.reduce((sum, s) => sum + s.net_settlement_amount, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold">Settlements & Reserves</h1>
        <p className="text-muted-foreground text-sm">Track your settlements and rolling reserve balance</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-grid">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Pending Settlements
            </CardTitle>
            <PiggyBank className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tabular-nums">
              {formatCurrency(totalPending)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {pendingSettlements.length} settlements pending
            </p>
          </CardContent>
        </Card>

        <Card className="card-grid">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Rolling Reserve Held
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tabular-nums">
              {formatCurrency(rollingReserve?.net_balance || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {merchant?.risk_config?.rolling_reserve_percentage || 5}% of transactions
            </p>
          </CardContent>
        </Card>

        <Card className="card-grid">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
              Releasable Reserve
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tabular-nums text-green-500">
              {formatCurrency(rollingReserve?.releasable || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              After {rollingReserve?.hold_period_days || 90} day hold period
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Settlement Cycle Info */}
      <Card className="card-grid">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-sm bg-primary/10">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Settlement Cycle</p>
                <p className="text-sm text-muted-foreground">
                  Your settlements are processed on a {merchant?.risk_config?.settlement_cycle || 'T+2'} basis
                </p>
              </div>
            </div>
            <Badge variant="outline" className="rounded-sm font-mono">
              {merchant?.risk_config?.settlement_cycle || 'T+2'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Settlements Table */}
      <Card className="card-grid">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Settlement History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-high-density">
              <TableHeader>
                <TableRow className="border-b border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider">Period</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Transactions</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Gross Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">MDR Deducted</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Reserve Held</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Net Settlement</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Scheduled Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settlements.length > 0 ? (
                  settlements.map((settlement) => (
                    <TableRow key={settlement.id} className="border-b border-border/50">
                      <TableCell className="text-sm">
                        {formatDate(settlement.period_start)} - {formatDate(settlement.period_end)}
                      </TableCell>
                      <TableCell className="font-mono tabular-nums">
                        {settlement.total_transactions}
                      </TableCell>
                      <TableCell className="font-mono tabular-nums">
                        {formatCurrency(settlement.gross_amount)}
                      </TableCell>
                      <TableCell className="font-mono tabular-nums text-muted-foreground">
                        -{formatCurrency(settlement.mdr_deducted)}
                      </TableCell>
                      <TableCell className="font-mono tabular-nums text-muted-foreground">
                        -{formatCurrency(settlement.rolling_reserve_held)}
                      </TableCell>
                      <TableCell className="font-mono tabular-nums font-medium">
                        {formatCurrency(settlement.net_settlement_amount)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(settlement.status)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(settlement.scheduled_date)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      <PiggyBank className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      No settlements yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
